import React, { useMemo, useCallback } from 'react';
import Icon from '@/components/AppIcon';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import Autocomplete from '@/components/ui/Autocomplete';
import Button from '@/components/ui/Button';
import useTranslation from '@/hooks/useTranslation';

const ConversationList = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  searchQuery,
  onSearchChange,
  directMessageQuery,
  onDirectMessageQueryChange,
  directMessageOptions,
  onSelectDirectRecipient,
  isCreatingDirectConversation = false,
  directMessageError = null,
  isSearchingDirectMessage = false,
  directMessageSearchError = null,
  onOpenGroupChat,
  onOpenAnnouncement,
  canCreateAnnouncement = false,
  className = '',
}) => {
  const { t, language } = useTranslation(['internalMessaging', 'common']);
  const translate = useCallback(
    (key, fallback) => t(`internalMessaging:${key}`, fallback),
    [t],
  );
  const locale = language || 'en';
  const filteredConversations = conversations?.filter((conv) =>
    conv?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    conv?.lastMessage?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const relativeTimeFormatter = useMemo(
    () =>
      new Intl.RelativeTimeFormat(locale, {
        numeric: 'auto',
      }),
    [locale],
  );
  const shortDateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }),
    [locale],
  );

  const formatTime = (timestamp) => {
    if (!timestamp) {
      return '';
    }
    const now = Date.now();
    const messageTime = new Date(timestamp);
    if (Number.isNaN(messageTime.getTime())) {
      return '';
    }
    const diffMinutes = Math.round((now - messageTime.getTime()) / (1000 * 60));
    if (Math.abs(diffMinutes) < 1) {
      return translate('conversationList.time.now', 'just now');
    }
    if (Math.abs(diffMinutes) < 60) {
      return relativeTimeFormatter.format(-diffMinutes, 'minute');
    }
    const diffHours = Math.round(diffMinutes / 60);
    if (Math.abs(diffHours) < 24) {
      return relativeTimeFormatter.format(-diffHours, 'hour');
    }
    return shortDateFormatter.format(messageTime);
  };

  const hasDirectMessageOptions = Array.isArray(directMessageOptions) && directMessageOptions.length > 0;

  const containerClasses = [
    'w-full lg:w-80 bg-card border-r border-border flex flex-col h-full',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground mb-3">
          {translate('conversationList.title', 'Messages')}
        </h2>

        {/* Search */}
        <div className="relative">
          <Icon
            name="Search"
            size={16}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
          />
          <input
            type="text"
            placeholder={translate('conversationList.searchPlaceholder', 'Search conversations...')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="mt-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            {translate('conversationList.newMessage', 'New message')}
          </p>
          <Autocomplete
            value={directMessageQuery}
            onChange={onDirectMessageQueryChange}
            options={directMessageOptions}
            onSelect={onSelectDirectRecipient}
            placeholder={
              hasDirectMessageOptions
                ? translate('conversationList.dmPlaceholder', 'Search employees…')
                : translate('conversationList.dmUnavailable', 'No employees available')
            }
            disabled={isCreatingDirectConversation}
            loading={isCreatingDirectConversation || isSearchingDirectMessage}
            emptyText={
              directMessageQuery
                ? translate('conversationList.dmNoResults', 'No employees found')
                : translate('conversationList.dmEmptyQuery', 'Start typing to search employees')
            }
            getOptionLabel={(option) => option?.name ?? option?.label ?? ''}
            getOptionDescription={(option) => option?.description ?? option?.email ?? option?.department ?? ''}
          />
          {directMessageSearchError && (
            <p className="mt-2 text-xs text-error" role="alert">
              {directMessageSearchError}
            </p>
          )}
          {directMessageError && (
            <p className="mt-2 text-xs text-error" role="alert">
              {directMessageError}
            </p>
          )}

          <div className="mt-4 space-y-2">
            <Button
              onClick={onOpenGroupChat}
              className="w-full justify-center"
            >
              <Icon name="Users" size={16} className="mr-2" />
              {translate('conversationList.startGroup', 'Start group chat')}
            </Button>

            {canCreateAnnouncement && (
              <Button
                onClick={onOpenAnnouncement}
                variant="secondary"
                className="w-full justify-center"
              >
                <Icon name="Megaphone" size={16} className="mr-2" />
                {translate('conversationList.makeAnnouncement', 'Make announcement')}
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations?.map((conversation) => {
          const unseenCount = (() => {
            const raw = conversation?.unseenCount ?? conversation?.unreadCount ?? 0;
            const numeric = Number(raw);
            return Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : 0;
          })();

          const isSelected = selectedConversation?.id === conversation?.id;
          const hasUnseen = !isSelected && unseenCount > 0;

          const itemClasses = [
            'p-4 border-b border-border cursor-pointer transition-colors',
            isSelected
              ? 'bg-primary/10 border-l-4 border-l-primary'
              : hasUnseen
                ? 'bg-primary/10 border-l-4 border-l-primary/60 ring-1 ring-primary/20'
                : 'hover:bg-muted',
          ].join(' ');

          return (
            <div
              key={conversation?.id}
              onClick={() => onSelectConversation(conversation)}
              className={itemClasses}
              data-unseen={hasUnseen ? 'true' : undefined}
            >
              <div className="flex items-start space-x-3">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                {conversation?.type === 'group' ? (
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                    <Icon name="Users" size={20} color="white" />
                  </div>
                ) : conversation?.type === 'announcement' ? (
                  <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                    <Icon name="Megaphone" size={20} color="white" />
                  </div>
                ) : (
                  <EmployeeAvatar
                    url={conversation?.avatarUrl}
                    name={conversation?.name}
                    alt={`${conversation?.name ?? translate('messagePanel.conversationFallback', 'Conversation')} avatar`}
                    size="md"
                    showStatus={false}
                  />
                )}
                
                {/* Online Status */}
                {conversation?.type === 'direct' && conversation?.isOnline && (
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-card"></div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3
                    className={`text-foreground truncate ${hasUnseen ? 'font-semibold' : 'font-medium'}`}
                  >
                    {conversation?.name}
                  </h3>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className="text-xs text-muted-foreground">
                      {formatTime(conversation?.timestamp)}
                    </span>
                    {unseenCount > 0 && (
                      <span className="bg-error text-error-foreground text-xs font-medium rounded-full px-2 py-1 min-w-[1.25rem] text-center">
                        {unseenCount > 99 ? '99+' : unseenCount}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {conversation?.lastMessageType === 'file' && (
                    <Icon name="Paperclip" size={14} className="text-muted-foreground flex-shrink-0" />
                  )}
                  {conversation?.lastMessageType === 'image' && (
                    <Icon name="Image" size={14} className="text-muted-foreground flex-shrink-0" />
                  )}
                  <p
                    className={`text-sm truncate ${hasUnseen ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
                  >
                    {conversation?.lastSender && conversation?.type !== 'direct' && (
                      <span className="font-medium">{conversation?.lastSender}: </span>
                    )}
                    {conversation?.lastMessage}
                  </p>
                </div>

                {/* Priority Badge */}
                {conversation?.priority === 'high' && (
                  <div className="flex items-center mt-1">
                    <Icon name="AlertTriangle" size={12} className="text-error mr-1" />
                    <span className="text-xs text-error font-medium">
                      {translate('conversationList.priorityHigh', 'High Priority')}
                    </span>
                  </div>
                )}
              </div>
              </div>
            </div>
          );
        })}

        {filteredConversations?.length === 0 && (
          <div className="p-8 text-center">
            <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? translate('conversationList.emptySearch', 'No conversations found')
                : translate('conversationList.emptyDefault', 'No messages yet')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;
